import { DotLottieReact, type DotLottie } from '@lottiefiles/dotlottie-react'
import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import { useGSAP } from '@/composables/useGSAP'

export type LottieOrImageProps = {
  animationData: unknown
  imageUrl: string
  className?: string
  loop?: boolean
  onDOMLoaded?: () => void
  appear?: boolean
}
export default function LottieOrImage({
  animationData,
  imageUrl,
  className,
  loop,
  onDOMLoaded,
  appear,
}: LottieOrImageProps) {
  const { gsap } = useGSAP()
  const [isSmall, setIsSmall] = useState(false)
  useLayoutEffect(() => {
    const onResize = () => {
      setIsSmall(window.innerWidth < 768)
    }
    window.addEventListener('resize', onResize)
    onResize()
    return () => window.removeEventListener('resize', onResize)
  }, [setIsSmall])

  // dotlottie-react 0.19 also invokes the ref callback with null when the canvas detaches (0.13 only
  // called it once the instance existed); keep onDOMLoaded's "instance is ready" contract
  const onDotLottieRef = useCallback(
    (instance: DotLottie | null) => {
      if (instance) onDOMLoaded?.()
    },
    [onDOMLoaded]
  )

  const imgRef = useRef<HTMLImageElement>(null)
  const onImageLoaded = useCallback(() => {
    onDOMLoaded?.()
    const ctx = gsap.context(() => {
      if (appear) {
        gsap
          .timeline()
          .fromTo(
            imgRef.current,
            {
              opacity: 0,
            },
            {
              opacity: 1,
              duration: 0.5,
              ease: 'quad.out',
            },
            0
          )
          .fromTo(
            imgRef.current,
            {
              scale: 0,
              rotate: -90,
            },
            {
              scale: 1,
              rotate: 0,
              duration: 0.7,
              ease: 'back.out(1.7)',
            },
            0
          )
      }
    })

    return () => ctx.kill()
  }, [gsap, appear, imgRef, onDOMLoaded])

  return isSmall ? (
    <img
      ref={imgRef}
      src={imageUrl}
      className={`${className ? className : ''} object-contain`}
      onLoad={onImageLoaded}
    />
  ) : (
    <DotLottieReact
      className={`${className ? className : ''}`}
      src={animationData as string}
      renderConfig={{ devicePixelRatio: 1 }}
      loop={loop}
      autoplay={true}
      useFrameInterpolation={false}
      dotLottieRefCallback={onDotLottieRef}
    />
    // <Lottie
    //   // @ts-expect-error renderer typing problem in lib, but canvas is supported
    //   renderer="canvas"
    //   className={`${className ? className : ''}`}
    //   animationData={animationData}
    //   loop={loop}
    //   onDOMLoaded={onDOMLoaded}
    // />
  )
}
